import { authClient } from './Common';
import { clearAllChatSelections } from '../components/Chat/utils/chatSelectionsStorage';

// Single source of truth for reading the current Okta access token — used
// wherever a request needs an `Authorization: Bearer` header or the WS
// handshake's auth subprotocol.
//
// Common.js configures the token manager with autoRenew: false, so a plain
// getTokens() call hands back an expired (or, with autoRemove: true,
// already-evicted) token once its ~1hr lifetime passes — which for a
// long-lived chat tab is routine, not an edge case. Renew explicitly here
// so every call site gets a live token without each of them re-implementing
// this check.
export async function getAccessToken(): Promise<string> {
  try {
    const { accessToken } = await authClient.tokenManager.getTokens();

    if (accessToken && !authClient.tokenManager.hasExpired(accessToken)) {
      return accessToken.accessToken;
    }

    const renewed = await authClient.tokenManager.renew('accessToken');
    return (renewed as any)?.accessToken ?? '';
  } catch {
    // Renewal itself failed — the underlying Okta session is gone, not just
    // the access token (e.g. it timed out, or was revoked elsewhere). Every
    // call site would otherwise fail on its own with a generic, confusing
    // error (a thrown fetch error, a stuck upload, a WS close event) and no
    // indication the user needs to log back in. Log them out properly
    // instead of just bouncing them to the login screen with stale Okta
    // tokens still sitting in storage.
    await forceLogout();
    return '';
  }
}

// Mirrors the resilient signOut + fallback shape already used by
// src/hooks/useLogout.ts (can't reuse that hook directly here — it's a
// React hook that reads useAuth(), and this is a plain module). Revokes
// tokens with Okta and clears local storage; if signOut itself fails (e.g.
// offline), falls back to a local-only tokenManager.clear() and a hard
// navigation to "/" — the real login route (there is no "/login" route in
// this app; src/services/api.ts's 401 handlers redirect there today, which
// is a separate, pre-existing bug left untouched here).
async function forceLogout() {
  if (window.location.pathname === '/') return;
  // Same as useLogout: each chat's saved picks only last until logout.
  clearAllChatSelections();
  try {
    await authClient.signOut({
      revokeAccessToken: true,
      revokeRefreshToken: true,
      postLogoutRedirectUri: window.location.origin,
    });
  } catch {
    try {
      await authClient.tokenManager.clear();
    } catch {
      /* ignore */
    }
    window.location.assign('/');
  }
}

// Raw ID token claims (email, given_name, family_name, ...), or null if
// unavailable. Replaces the duplicated `authClient.tokenManager.getTokens()`
// + claims destructuring that used to live in AuthContext and useUserInfo.
export async function getIdTokenClaims(): Promise<any | null> {
  try {
    const { idToken } = await authClient.tokenManager.getTokens();
    return idToken?.claims ?? null;
  } catch {
    return null;
  }
}

// Resolves the current user's email from the ID token claims, falling back
// to the userinfo endpoint if the claim is missing. Replaces the identical
// logic duplicated in useUserInfo.tsx and FeedbackPopover.tsx.
export async function resolveUserEmail(): Promise<string | null> {
  try {
    const { idToken, accessToken } = await authClient.tokenManager.getTokens();
    let email = (idToken?.claims as any)?.email as string | undefined;
    if (!email && accessToken) {
      const userInfo = await authClient.token.getUserInfo(accessToken, idToken);
      email = (userInfo as any)?.email as string | undefined;
    }
    return email ?? null;
  } catch {
    return null;
  }
}
